import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Input, Icon, Alert } from 'antd';
import { extractDomainAndHomepage } from '../../utils';
import useGetPlanSettings from '../../../sales/hooks/useGetPlanSettings';
import { useFetchWebsiteIndexEntriesQuery, useLazyValidateDomainQuery } from '../service';


type Props = {
    onValidDomain: (domain: string) => void;
    title?: string;
};


export default function AddNewWebsiteForm({ title, onValidDomain }: Props) {
    const [inputDomain, setInputDomain] = useState<string>('');
    const [isValidDomain, setIsValidDomain] = useState<boolean>(false);
    const [inputDomainError, setInputDomainError] = useState<string>('');
    const [showMaxDomainsError, setShowMaxDomainsError] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    const { data: websiteIndexEntries, isLoading: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const existingWebsiteDomains = websiteIndexEntries ? websiteIndexEntries.map((entry) => entry.domain) : [];

    const [triggerValidateDomain, { isLoading: isValidatingDomain }] = useLazyValidateDomainQuery();
    const { dpMaxDomains, isLoading: isLoadingSubscriptionPlanSettings } = useGetPlanSettings();

    const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const domain = event.target.value.trim().toLowerCase();
        setInputDomainError("");
        setShowMaxDomainsError(false);
        setInputDomain(domain);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.stopPropagation();
        }
    };

    async function validateDomain() {
        const { domain, error: invalidDomainError } = extractDomainAndHomepage(inputDomain);

        if (invalidDomainError) {
            setInputDomainError(invalidDomainError);
            return;
        }

        if (domain && existingWebsiteDomains.includes(domain)) {
            setInputDomainError("You already monitor this domain!");
            return;
        }

        if (!websiteIndexEntries || !dpMaxDomains) {
            return;
        }

        if (websiteIndexEntries.length >= dpMaxDomains) {
            setShowMaxDomainsError(true);
            return;
        }

        if (domain) {
            try {
                await triggerValidateDomain({ domain }, true).unwrap();
            } catch (error) {
                setInputDomainError("Invalid domain name!");
                return;
            }
            setIsValidDomain(true);
            onValidDomain(domain);
        }

        return;
    };


    useEffect(() => {
        const listener = async (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === "NumpadEnter") {
                event.preventDefault();
                const buttonElement = buttonRef.current;
                if (buttonElement) {
                    const nextButton = buttonElement.querySelector('.next-btn');
                    if (nextButton) {
                        (nextButton as HTMLButtonElement).click();
                    }
                }
            }
        };

        const formElement = containerRef.current;
        formElement?.addEventListener('keydown', listener);

        return () => {
            formElement?.removeEventListener('keydown', listener);
        };
    }, []);

    const nextButton = (
        <div className="button-wrapper next-button" ref={buttonRef}>
            <Button
                className="next-btn right"
                type="primary"
                size="large"
                loading={isValidatingDomain || isValidDomain}
                onClick={validateDomain}
                disabled={isLoadingWebsiteIndexEntries || isLoadingSubscriptionPlanSettings || showMaxDomainsError}
            >
                {(isValidatingDomain || isValidDomain) ? "" : "Next"}
            </Button>
        </div>
    );
    return (
        <div className="add-website-index-entry-form">
            {title && <div className="add-new-website-form-title" dangerouslySetInnerHTML={{ __html: title }} />}

            <Form>
                <div ref={containerRef}>
                    <Form.Item
                        validateStatus={inputDomainError ? "error" : "validating"}
                        help={inputDomainError ? inputDomainError : ""}
                        style={{ marginBottom: 0 }}
                    >
                        <Input
                            id="main_url"
                            className="website-input"
                            autoCorrect="off"
                            autoCapitalize="off"
                            autoComplete="off"
                            prefix={<Icon type="global" style={{ color: "rgba(0,0,0,.25)", fontSize: "20px" }} />}
                            disabled={isLoadingWebsiteIndexEntries}
                            placeholder="example.com"
                            name="url"
                            size="large"
                            value={inputDomain}
                            onChange={handleDomainChange}
                            onKeyDown={handleKeyDown}
                            addonAfter={nextButton}
                        />
                    </Form.Item>

                    {showMaxDomainsError && (
                        <Form.Item>
                            <Alert
                                className="deep-analysis-website-form-alert"
                                type="error"
                                showIcon
                                message="Looks like you've hit your domain limit!"
                            />
                        </Form.Item>
                    )}
                </div>
            </Form>
        </div>
    );
}
